import { IActions } from '@/interfaces/Store';
import { IThumbedReview } from '@/interfaces/IThumbedReview';
import { IThumbReviewResult } from '@/interfaces/IThumbReviewResult';

import mutations from './mutations';
import state from './state';
import { getLocalStorage } from './storage';
import { IUniversity } from '@/interfaces/IUniversity';

declare const REVIEWS_API_ENDPOINT: string;

const getDataFromApi = async <T>(relativeUrl: string): Promise<T | null> => {
	const url = `${REVIEWS_API_ENDPOINT}/${relativeUrl}`;
	const result = await fetch(url);

	if (result.ok) {
		return (await result.json()) as T;
	}

	return null;
};

const actions: IActions = {
	setLoggedInState: (loggedInState: boolean) => {
		mutations.setLoggedInState(loggedInState);
	},
	loadUniversities: async (): Promise<void> => {
		mutations.setUniversities((await getDataFromApi('v1/universities')) ?? []);
	},
	loadUniversity: async (id: number): Promise<void> => {
		try {
			const universities = state.universities;
			const index = universities.findIndex((u) => u.id === id);

			if (index >= 0) {
				return;
			}

			const university = (await getDataFromApi(`v1/universities/${id}`)) as IUniversity;
			if (university) {
				universities.push(university);
				mutations.setUniversities(universities);
			}
		} catch (e) {
			console.error(`An error occurred while fetching university with id '${id}: `, e);
		}
	},
	flagReview: async (reviewId: number): Promise<void> => {
		const isFlagged = state.flaggedReviews.indexOf(reviewId) !== -1;
		if (isFlagged) {
			await mutations.flaggedReviewsRemove(reviewId);
		} else {
			await mutations.flaggedReviewsAdd(reviewId);
		}
	},
	thumbReview: async (review: IThumbedReview): Promise<IThumbReviewResult> => {
		const reviewStored = state.thumbedReviews.find((r) => r.reviewId === review.reviewId);
		if (reviewStored === undefined) {
			await mutations.thumbedReviewsAdd(review);

			return {
				state: 'added',
				valence: review.valence,
			};
		} else if (reviewStored.valence !== review.valence) {
			await mutations.thumbedReviewsUpdate(review);

			return {
				state: 'changed',
				valence: review.valence,
			};
		} else {
			await mutations.thumbedReviewsRemove(review);

			return {
				state: 'removed',
				valence: review.valence,
			};
		}
	},
	loadLocalStorage: async (): Promise<void> => {
		const storage = await getLocalStorage();

		if (storage.flaggedReviews && Array.isArray(storage.flaggedReviews)) {
			for (const reviewId of storage.flaggedReviews) {
				await mutations.flaggedReviewsAdd(reviewId);
			}
		}

		if (storage.thumbedReviews && Array.isArray(storage.thumbedReviews)) {
			for (const thumbedReview of storage.thumbedReviews) {
				await mutations.thumbedReviewsAdd(thumbedReview);
			}
		}
	},
	showWidgetReviewsAll: (popupState: boolean): void => {
		mutations.showWidgetReviewsAll(popupState);
	},
	loadWidgetReviewsAll: (popupState: boolean): void => {
		mutations.loadWidgetReviewsAll(popupState);
	},
	loadGssaResult: async (organisation_id: number): Promise<void> => {
		const gssaResults = state.gssaResults;

		gssaResults.set(organisation_id, await getDataFromApi(`v2/gssa/${organisation_id}/2025`));
		mutations.setGssaResults(gssaResults);
	},

	loadReviewsPerUniversity: async (id: number): Promise<void> => {
		const reviewsMap = state.reviewsPerUniversity;

		reviewsMap.set(id, (await getDataFromApi(`v1/reviews?organisation_id=${id}`)) ?? []);
		mutations.setReviewsPerUniversity(reviewsMap);
	},

	showWidgetReviewsAllPerUniversity: (id: number, popupState: boolean): void => {
		const popupMap = state.showPopupPerUniversity;

		popupMap.set(id, popupState);
		mutations.showWidgetReviewsAllPerUniversity(popupMap);
	},
};

export default actions;
